import React, { useEffect, useState } from 'react'
import { ScrollArea } from '../../../components/ui/scroll-area'
import Checkbox from '../../../components/ui/checkbox'
import { AlertCircleIcon, GripIcon, Timer, XIcon, FilePenIcon, PauseCircleIcon, CheckCircleIcon, CheckIcon, TruckIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { Label } from '../../../components/ui/label'
import { useBoundStore } from '../../../zustand'
import { DatePickerWithRange } from '../../../components/ui/date-picker-with-range'
import { DateRange } from 'react-day-picker'
import { useInterventionInput } from '../../../context/InterventionInputContext'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../../../context/AuthProvider';
import axios from '../../../lib/axios';
import { Skeleton } from '../../../components/ui/skeleton'
import { isWithinRange } from '../../../lib/time'
import { addDays, addMonths, endOfDay, format, startOfDay, isSameDay } from 'date-fns'
import { Trans, useLingui } from '@lingui/react/macro'
import { DatePicker } from '../../../components/ui/datepicker'
import { getCustomSettings } from '../../../config/custom-settings'
import { checkForAppUpdate } from '../../../hooks/use-version-check'
import { Button } from '../../../components/ui/button'
import { Link } from 'react-router-dom'

type Props = {
  task_id?: number;
  date?: DateRange
  settask_id?: (task: number) => void
  setdate?: (date: DateRange | undefined) => void

  show_all?: boolean
  setshow_all?: (show_all: boolean) => void
}

// Role names come from the API user roles table (ROLENAME), compared case-insensitively
const SUBCONTRACTOR_ROLES = ['contractor', 'external technician']

// Own technicians can look 1 day ahead, subcontractors 1 month. Other roles are unrestricted.
function getMaxPlanningDate(role?: string) {
  const normalizedRole = role?.trim().toLowerCase() ?? ''
  if (normalizedRole === 'technician') return addDays(new Date(), 1)
  if (SUBCONTRACTOR_ROLES.includes(normalizedRole)) return addMonths(new Date(), 1)
  return undefined
}

const HomeRideButton = () => (
  <>
    <Link to="/thuisrit">
      <Button className='w-full'><TruckIcon /> Thuisrit</Button>
    </Link>
  </>
)

const Sidebar = ({ task_id, date, settask_id, setdate, show_all, setshow_all }: Props) => {
  const { t, i18n } = useLingui()

  const interventions = useBoundStore((state) => state.interventions)
  const { intervention: activeIntervention } = useInterventionInput()

  const setInterventions = useBoundStore((state) => state.setInterventions)

  const { auth } = useAuth()

  const [is_open, setis_open] = useState(false);

  const variants = {
    initial: {
      x: '100%',
      opacity: 0,
      width: 0
    },
    animate: {
      x: 0,
      opacity: 1,
      width: 'auto',
      transition: {
        delayChildren: 0,
        staggerChildren: 0.1
      }

    },
    exit: {
      x: '100%',
      opacity: 0,
      width: 0
    }
  }

  const item = {
    initial: { x: 10, opacity: 0 },
    animate: {
      x: 0,
      opacity: 1
    }
  }

  const { isLoading: isLoadingInterventions } = useQuery({
    queryKey: [`interventions`],
    queryFn: async () => {
      await checkForAppUpdate();
      let params: any = {
        dbid: auth.data?.databases[0].dbid
      }
      if (date?.from) {
        params.fromdate = format(new Date(date.from), 'yyyyMMdd')
      }
      if (date?.to) {
        params.todate = format(new Date(date.to), 'yyyyMMdd')
      }

      const { data } = await axios.get(`interventions`, {
        params: params
      })
      setInterventions(data);
      return data;
    }
  })

  const enabledhometrip = getCustomSettings(auth.data?.databases[0].dbid).intervention?.settings?.enable_hometrip
  const selectedDay = date?.from ?? new Date()
  const showHomeRide = Boolean(enabledhometrip && isSameDay(selectedDay, new Date()))

  const maxPlanningDate = getMaxPlanningDate(auth.data?.databases[0].role)
  const calendarDisabled = maxPlanningDate ? { after: maxPlanningDate } : undefined

  function handleClickTask(call_id: number | string) {
    // setSearchParams({ callid: call_id.toString() })
    settask_id && settask_id(Number(call_id))
  }

  function returnStatusIcon(status: number) {
    switch (status) {
      case 1:
        return <AlertCircleIcon className='w-4 h-4 text-purple-600' />
      case 5:
        return <Timer className='w-4 h-4 text-orange-600' />
      case 7:
        return <FilePenIcon className='w-4 h-4 text-red-600' />
      case 8:
        return <PauseCircleIcon className='w-4 h-4 text-neutral-700' />
      case 9:
        return <CheckCircleIcon className='w-4 h-4 text-green-600' />
    }
  }

  function filterInterventions() {
    if (!Array.isArray(interventions)) return []
    let tmp = [...interventions]

    if (!show_all) {
      const filterStatus = getCustomSettings(auth.data?.databases[0].dbid)?.intervention?.start_page?.filter_status || []
      tmp = tmp.filter(i => !filterStatus.includes(i.m_iSTATUS))
      tmp = tmp.filter(i => {
        const inRange = isWithinRange(new Date(i.m_dDATUM), startOfDay(date?.from || new Date()), endOfDay(date?.to || new Date()))
        return inRange
      })
    }

    return tmp
  }

  return (
    <>
      <div className='hidden sm:block sticky h-[calc(100dvh-64px)] top-16 border-r border-r-neutral-200 w-[225px]'>
        <ScrollArea className='h-full'>
          <section className='flex flex-col gap-2 py-2'>
            <div className='flex items-center justify-between px-2'>
              <Label htmlFor='show-all' className='text-base font-semibold'><Trans>Toon alles</Trans></Label>
              <Checkbox checked={show_all} onCheckedChange={(c) => setshow_all && setshow_all(c)} id='show-all' />
            </div>
            <div className='px-2 overflow-auto'>
              <DatePicker
                value={date?.from}
                calendarDisabled={calendarDisabled}
                onDateChange={(v) => {
                  if (!v) return;
                  setdate?.({
                    from: v,
                    to: v
                  })
                }}
              />
            </div>
            <div className='flex flex-col w-full'>
              {isLoadingInterventions && (!filterInterventions() || filterInterventions().length === 0) &&
                <>
                  {Array(5).fill('').map((_, i) => (
                    <div className='px-2 py-1' key={`skeleton-${i}`}>
                      <Skeleton className='w-full h-20 rounded-md' />
                    </div>
                  ))}
                </>
              }

              {interventions && filterInterventions()?.map((intervention, i) => (
                <button onClick={() => { handleClickTask(intervention.m_iCALLID) }} className={`${task_id === intervention.m_iCALLID ? 'border-r-primary bg-primary text-primary-foreground' : 'border-r-transparent'
                  } 
              duration-200 p-2 flex flex-col justify-between gap-4 border-r-2 border-t last:border-b`} key={i}>
                  <div>
                    <p className='text-sm font-semibold text-left'>{intervention?.m_sTYPE}</p>
                    <p className='text-xs text-left text-neutral-400 line-clamp-2'>{intervention?.m_sOMSCHRIJVING}</p>
                  </div>

                  <div className='flex items-end justify-between w-full gap-4'>
                    <div>
                      <p className='text-xs text-left opacity-50'>{intervention.m_oKLANT.m_sNAAM}</p>
                      <p className='text-xs text-left opacity-50'>{intervention?.m_dDATUM}</p>
                      <p className='text-xs text-left opacity-50'>{intervention.m_tTIJD?.substring(0, 5)} - {intervention.m_tEINDTIJD?.substring(0, 5)}</p>
                    </div>
                    <div className='flex items-center gap-1'>
                      {intervention.m_bCHECKINATWORK && <div className='flex items-center justify-center w-4 h-4 overflow-hidden rounded-full'><img className='object-cover w-full h-full' src="/images/checkinatwork.png" /></div>}
                      {intervention.m_iINTERVENTIESID === activeIntervention?.m_iINTERVENTIESID
                        ? returnStatusIcon(5)
                        : returnStatusIcon(intervention.m_iSTATUS)
                      }
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {showHomeRide &&
              <div className='px-2'>
                <HomeRideButton />
              </div>
            }


          </section>
        </ScrollArea>
      </div>

      <div className={`${is_open && 'bg-black/60 backdrop-blur-sm'} sm:hidden fixed top-16 right-0 p-2 z-10 flex flex-col items-end gap-2 h-[calc(100dvh-64px)]`}>

        <button onClick={() => { setis_open(!is_open) }} className='w-10 h-10 min-h-[40px] rounded-full border flex items-center justify-center bg-white'>{!is_open ? <GripIcon className='w-4 h-4' /> : <XIcon className='w-4 h-4' />}</button>

        <ScrollArea className='h-full rounded-md'>
          <AnimatePresence>
            {is_open &&
              <>
                <motion.div variants={variants} exit='exit' initial='initial' animate='animate' className='flex flex-col gap-2 p-2 bg-white border-t border-l'>
                  <div className='flex items-center justify-between px-2'>
                    <Label htmlFor='show-all-sm' className='text-base font-semibold'><Trans>Toon alles</Trans></Label>
                    <Checkbox checked={show_all} onCheckedChange={(c) => setshow_all && setshow_all(c)} id='show-all-sm' />
                  </div>
                  <div className='px-2 overflow-auto'>
                    <DatePicker
                      value={date?.from}
                      calendarDisabled={calendarDisabled}
                      onDateChange={(v) => {
                        if (!v) return;
                        setdate?.({
                          from: v,
                          to: v
                        })
                      }}
                    />
                  </div>
                </motion.div>
                <motion.div variants={variants} exit='exit' initial='initial' animate='animate' className='flex flex-col bg-white border-l shadow-xl'>
                  {interventions && filterInterventions() && filterInterventions().map((intervention, i) => (
                    <motion.button variants={item} onClick={() => { settask_id && settask_id(intervention.m_iCALLID) }} className={`${task_id === intervention.m_iCALLID ? 'border-l-primary bg-primary text-primary-foreground' : 'border-l-transparent'} duration-200 p-2 flex flex-col justify-between gap-4 border-l-2 border-t last:border-b`} key={i}>
                      <div>
                        <p className='text-sm font-semibold text-left'>{intervention?.m_sTYPE}</p>
                        <p className='text-xs text-left text-neutral-400 line-clamp-2'>{intervention?.m_sOMSCHRIJVING}</p>
                      </div>
                      <div className='flex items-end justify-between w-full gap-4'>
                        <div>
                          <p className='text-xs text-left text-neutral-400'>{intervention.m_oKLANT.m_sNAAM}</p>
                          <p className='text-xs text-left opacity-50'>{intervention?.m_dDATUM}</p>
                          <p className='text-xs text-left text-neutral-400'>{intervention.m_tTIJD?.substring(0, 5)} - {intervention.m_tEINDTIJD?.substring(0, 5)}</p>
                        </div>
                        <div className='flex items-center gap-1'>
                          {intervention.m_bCHECKINATWORK && <div className='flex items-center justify-center w-4 h-4 overflow-hidden rounded-full'><img className='object-cover w-full h-full' src="/images/checkinatwork.png" /></div>}
                          {intervention.m_iINTERVENTIESID === activeIntervention?.m_iINTERVENTIESID
                            ? returnStatusIcon(5)
                            : returnStatusIcon(intervention.m_iSTATUS)
                          }
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </motion.div>
                <motion.div variants={variants} exit='exit' initial='initial' animate='animate' className='flex flex-col gap-2 p-2 bg-white border-t border-l'>
                  {showHomeRide &&
                    <div className='px-2'>
                      <HomeRideButton />
                    </div>
                  }
                </motion.div>
              </>
            }
          </AnimatePresence>
        </ScrollArea>

      </div>
    </>
  )
}

export default Sidebar